/**
 * VPCs Tab
 *
 * Displays customer VPCs and highlights which one is connected to the gateway.
 * Read-only view (CRUD is done in NetworkingPage).
 */

import { useQuery } from '@tanstack/react-query'
import {
  NetworkIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  GlobeIcon,
  LinkIcon,
} from 'lucide-react'

import { vpcApi } from '@nebula/shared/services'
import type { VPCListItem, VPCAllocatedIPsResponse } from '@nebula/shared/services'

type VPCsTabProps = {
  gatewayVnetId?: string
}

export default function VPCsTab({ gatewayVnetId }: VPCsTabProps) {
  const {
    data: vpcsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customer-vpcs'],
    queryFn: vpcApi.listCustomerVPCs,
  })

  const vpcs = vpcsData?.vpcs || []

  // Find the connected VPC to fetch its allocated IPs
  const connectedVpc = gatewayVnetId ? vpcs.find((v) => v.vnet_id === gatewayVnetId) : null

  const { data: allocatedIPs } = useQuery({
    queryKey: ['vpc-allocated-ips', connectedVpc?.id],
    queryFn: () => vpcApi.getVPCAllocatedIPs(connectedVpc!.id),
    enabled: !!connectedVpc,
  })

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center space-x-2">
          <NetworkIcon className="size-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">VPCs</h3>
          {vpcs.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
              {vpcs.length}
            </span>
          )}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
        >
          <RefreshCwIcon className={`mr-1.5 size-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-6 animate-spin text-purple-600" />
        </div>
      ) : error ? (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangleIcon className="size-5 shrink-0 text-red-500" />
          <span className="text-sm text-red-700">Erro ao carregar VPCs.</span>
        </div>
      ) : vpcs.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <GlobeIcon className="mx-auto mb-3 size-10 text-gray-300" />
          <p className="text-sm">Nenhuma VPC encontrada.</p>
          <p className="mt-1 text-xs text-gray-400">
            VPCs podem ser criadas na página de Networking.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Sort: connected VPC first */}
          {sortVPCs(vpcs, gatewayVnetId).map((vpc) => {
            const isConnected = gatewayVnetId === vpc.vnet_id
            return (
              <VPCCard
                key={vpc.id}
                vpc={vpc}
                isConnected={isConnected}
                allocatedIPs={isConnected ? allocatedIPs : undefined}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function sortVPCs(vpcs: VPCListItem[], gatewayVnetId?: string): VPCListItem[] {
  if (!gatewayVnetId) return vpcs
  return [...vpcs].sort((a, b) => {
    if (a.vnet_id === gatewayVnetId) return -1
    if (b.vnet_id === gatewayVnetId) return 1
    return 0
  })
}

type VPCCardProps = {
  vpc: VPCListItem
  isConnected: boolean
  allocatedIPs?: VPCAllocatedIPsResponse
}

function VPCCard({ vpc, isConnected, allocatedIPs }: VPCCardProps) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        isConnected ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200 bg-white'
      }`}
    >
      {/* VPC Header */}
      <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center space-x-2">
          <NetworkIcon
            className={`size-5 shrink-0 ${isConnected ? 'text-purple-600' : 'text-gray-400'}`}
          />
          <h4 className="truncate font-semibold text-gray-900">{vpc.name}</h4>
          {isConnected && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
              <LinkIcon className="mr-1 size-3" />
              Conectada ao Gateway
            </span>
          )}
          {vpc.is_default && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              Default
            </span>
          )}
        </div>
      </div>

      {/* VPC Info Grid */}
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-gray-500">VNET ID</dt>
          <dd className="font-mono text-gray-900">{vpc.vnet_id}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">CIDR</dt>
          <dd className="font-mono text-gray-900">{vpc.cidr}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Gateway IP</dt>
          <dd className="font-mono text-gray-900">{vpc.gateway}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">VNI</dt>
          <dd className="font-mono text-gray-900">{vpc.vni}</dd>
        </div>
      </div>

      {/* Allocations summary */}
      <div className="mt-3 border-t border-gray-100 pt-3">
        <span className="text-xs text-gray-500">
          {vpc.current_allocations} IP{vpc.current_allocations === 1 ? '' : 's'} alocado
          {vpc.current_allocations === 1 ? '' : 's'}
        </span>
      </div>

      {/* Allocated IPs table (only for connected VPC) */}
      {isConnected && allocatedIPs && allocatedIPs.allocated_ips.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <h5 className="mb-2 text-xs font-medium text-gray-700">IPs Alocados</h5>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                    IP
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                    VM
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody>
                {allocatedIPs.allocated_ips.map((alloc, index) => (
                  <tr
                    key={`${alloc.ip}-${index}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-2 py-1.5 font-mono text-gray-900">{alloc.ip}</td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {alloc.vm_name || alloc.vm_id || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-500">
                      {alloc.allocated_at ? (
                        new Date(alloc.allocated_at).toLocaleDateString('pt-BR')
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
